import { Controller } from "@hotwired/stimulus"
import { AudioCapture } from "../audio/capture"
import { AudioPlayer } from "../audio/player"

// 音声面談のオーケストレータ。
// ターンループ: AI読み上げ(ai_speaking) → 聞き取り(listening) → VADでターン終了
//   → SSE処理(processing) → AI読み上げ → … → 完了
// マイク不可・エラー時はテキスト入力にフォールバックする（テキストは常時併用可）。
export default class extends Controller {
  static targets = [
    "overlay", "startButton", "overlayNote",
    "status", "statusDot", "messages",
    "progressBar", "progressLabel",
    "subtitle", "subtitleText",
    "micBar", "doneButton", "replayButton", "skipButton", "textForm", "textInput", "textSubmit", "toggleTextLink",
    // 「どちらの番か」を大きく示すバナー（任意。設置したビューだけで表示する）
    "turnBanner", "turnBannerText", "turnBannerIcon"
  ]
  static values = {
    turnsUrl: String,
    completeUrl: String,
    answered: Number,
    total: Number,
    // 表示ラベル（任意）。面談は既定値、営業ロープレなど別フローで上書きする。
    aiName: String,        // チャットログのAI話者名（既定「田中」）
    progressUnit: String,  // 進捗の単位（既定「問」）
    finishedText: String   // 完了時のステータス表示（既定「面談が完了しました」）
  }

  connect() {
    this.player = new AudioPlayer()
    this.capture = new AudioCapture({
      onSpeechEnd: (base64) => this.handleSpeechEnd(base64),
      onSpeechStart: () => this.setStatus("listening_active")
    })
    this.voiceAvailable = false
    this.textMode = false // テキスト入力中は音声の聞き取りをオフにする（排他）
    this.busy = false
    this.skipping = false // AI発話スキップ中フラグ（後続の音声チャンクを無視する）
    this.scrollToBottom()
  }

  disconnect() {
    this.capture?.stop()
  }

  // 「面談を開始する」ボタン（ユーザー操作でAudioContext・マイクを初期化）
  async start() {
    this.startButtonTarget.disabled = true
    await this.player.init(16000)

    try {
      await this.capture.start()
      this.voiceAvailable = true
    } catch {
      // マイク不許可・非対応 → テキストモードへフォールバック
      this.voiceAvailable = false
      this.textMode = true
      this.showTextForm(true)
      this.appendNotice("マイクが利用できないため、テキスト入力でご回答ください。")
    }

    this.overlayTarget.classList.add("hidden")

    // 直近のAI発話（初回は挨拶）を読み上げてからターン開始
    await this.replayLastUtterance()
  }

  // 聞き直しボタン: 直近のAI発話を再読み上げ
  async replay() {
    if (this.busy) return
    await this.replayLastUtterance()
  }

  // スキップボタン: AI発話の読み上げを途中で止めて次へ進む。
  // stop()で再生中の音声を止め、skippingフラグで以降のストリーミング音声チャンクの
  // 再生も抑制する（TTSは文単位で逐次届くため、stop()だけでは後続チャンクが鳴ってしまう）。
  // waitForEnd()が即解決し、ターンループがそのまま次の聞き取りへ進む。
  skip() {
    if (!this.player.playing) return
    this.skipping = true
    this.player.stop()
  }

  // テキスト入力フォームの表示切替（常時用意するフォールバック）。
  // テキスト入力中は音声の聞き取りを止め、誤って音声ターンが送信されないようにする。
  toggleText(event) {
    event?.preventDefault()
    this.textMode = this.textFormTarget.classList.contains("hidden")
    this.showTextForm(this.textMode)

    if (this.textMode) {
      this.capture.setListening(false) // 蓄積中の音声も破棄
      if (!this.busy) this.setStatus("text_mode")
    } else if (!this.busy) {
      this.startListening() // テキストを閉じたら音声の聞き取りに戻る
    }
  }

  // テキスト送信（音声と同じSSEエンドポイントへ）
  async submitText(event) {
    event.preventDefault()
    const text = this.textInputTarget.value.trim()
    if (!text || this.busy) return
    this.textInputTarget.value = ""
    await this.processTurn({ text })
  }

  // 「話し終わりました」ボタン: 蓄積した発話を送信して明示的にターンを終える
  finishSpeaking() {
    if (this.busy) return
    this.capture.finish()
  }

  // --- ターン処理 ---

  async handleSpeechEnd(base64) {
    if (this.busy) return

    // 発話が無い・短すぎるままボタンが押された場合は聞き取りに戻る
    if (!base64) {
      this.appendNotice("音声がうまく拾えませんでした。もう一度お話しいただくか、テキストでご回答ください。")
      this.startListening()
      return
    }
    await this.processTurn({ audio: base64 })
  }

  async processTurn(payload) {
    this.busy = true
    this.skipping = false
    this.capture.setListening(false)
    this.setStatus("processing")

    try {
      const done = await this.streamTurn(payload)

      if (done?.skipped) {
        // 聞き取れなかった → そのまま聞き取りに戻る
        this.appendNotice("うまく聞き取れませんでした。もう一度お話しください。")
        this.busy = false
        this.startListening()
        return
      }

      this.updateProgress(done.answered, done.total)

      // AI音声の再生完了を待ってから次へ
      await this.player.waitForEnd()

      if (done.finished) {
        this.setStatus("finished")
        setTimeout(() => { window.location.href = this.completeUrlValue }, 2000)
        return
      }

      this.busy = false
      this.startListening()
    } catch (error) {
      this.appendNotice(error.message || "通信に失敗しました。もう一度お試しください。")
      this.busy = false
      this.startListening()
    }
  }

  async replayLastUtterance() {
    this.busy = true
    this.skipping = false
    this.capture.setListening(false)
    try {
      const done = await this.streamTurn({ replay: "1" })
      this.updateProgress(done.answered, done.total)
      await this.player.waitForEnd()
    } catch {
      // 読み上げ失敗時もテキストで継続できるようにする
      this.appendNotice("音声の再生に失敗しました。テキストでもご回答いただけます。")
    }
    this.busy = false
    this.startListening()
  }

  // SSEレスポンスを処理し、doneイベントを返す
  async streamTurn(payload) {
    const response = await fetch(this.turnsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(payload)
    })
    if (!response.ok || !response.body) throw new Error("通信に失敗しました。もう一度お試しください。")

    // 再読み上げ時のAI発話は既に画面に表示済みなので、メッセージを重複追加しない
    const isReplay = payload.replay === "1"

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let done = null
    let aiSubtitleShown = false

    for (;;) {
      const { value, done: streamEnded } = await reader.read()
      if (streamEnded) break
      buffer += decoder.decode(value, { stream: true })

      let sepIndex
      while ((sepIndex = buffer.indexOf("\n\n")) >= 0) {
        const rawEvent = buffer.slice(0, sepIndex)
        buffer = buffer.slice(sepIndex + 2)

        const dataLine = rawEvent.split("\n").find((l) => l.startsWith("data:"))
        if (!dataLine) continue
        const event = JSON.parse(dataLine.slice(5).trim())

        switch (event.type) {
          case "transcript":
            if (event.role === "user") {
              this.appendMessage("candidate", event.text)
            } else {
              if (!isReplay) this.appendMessage("ai", event.text)
              this.showSubtitle(event.text)
              if (!aiSubtitleShown) { this.setStatus("ai_speaking"); aiSubtitleShown = true }
            }
            break
          case "audio":
            // スキップ後に届く音声チャンクは再生しない（質問文はログ・字幕に残す）
            if (!this.skipping) this.player.enqueue(event.data, event.sampleRate)
            break
          case "done":
            done = event
            break
          case "error":
            throw new Error(event.message)
        }
      }
    }

    if (!done) throw new Error("応答が中断されました。もう一度お試しください。")
    return done
  }

  // --- UI ---

  startListening() {
    if (this.voiceAvailable && !this.textMode) {
      this.capture.setListening(true)
      this.setStatus("listening")
    } else {
      this.setStatus("text_mode")
    }
  }

  setStatus(state) {
    const aiName = this.aiNameValue || "田中"
    const config = {
      processing:       { text: "考えています…",                dot: "bg-amber-400 animate-pulse", mic: false,
                          banner: { text: `${aiName}が考えています`, tone: "wait" } },
      ai_speaking:      { text: "話し中",                        dot: "bg-indigo-500 animate-pulse", mic: false, skip: true,
                          banner: { text: `${aiName}が話しています`, tone: "speaking" } },
      listening:        { text: "聞いています — お話しください",  dot: "bg-green-500",               mic: true,
                          banner: { text: "あなたの番です　話しかけてください", tone: "your_turn", micIcon: true } },
      listening_active: { text: "聞いています…",                 dot: "bg-green-500 animate-pulse", mic: true,
                          banner: { text: "聞いています…　話し終わったらボタンを押してください", tone: "your_turn", micIcon: true } },
      text_mode:        { text: "テキストでご回答ください",        dot: "bg-gray-400",                mic: false,
                          banner: { text: "あなたの番です　テキストで入力してください", tone: "your_turn" } },
      finished:         { text: this.finishedTextValue || "面談が完了しました", dot: "bg-green-500",     mic: false,
                          banner: { text: this.finishedTextValue || "面談が完了しました", tone: "wait" } }
    }[state]
    if (!config) return

    this.statusTarget.textContent = config.text
    this.statusDotTarget.className = `inline-block h-2.5 w-2.5 rounded-full ${config.dot}`
    this.micBarTarget.classList.toggle("hidden", !config.mic)
    // 聞き取り中のみ「話し終わりました」ボタンを表示
    this.doneButtonTarget.classList.toggle("hidden", !config.mic)
    // AI発話中のみ「読み上げをスキップ」ボタンを表示
    this.skipButtonTarget.classList.toggle("hidden", !config.skip)
    this.renderTurnBanner(config.banner)
  }

  // 「どちらの番か」を色と文字で大きく示す（バナーを置いたビューのみ）。
  renderTurnBanner(banner) {
    if (!this.hasTurnBannerTarget || !banner) return

    const tones = {
      wait:      "border-slate-200 bg-slate-50 text-slate-500",
      speaking:  "border-indigo-200 bg-indigo-50 text-indigo-700",
      your_turn: "border-green-400 bg-green-50 text-green-800 shadow-soft"
    }
    this.turnBannerTarget.className =
      `mb-4 flex items-center justify-center gap-2 rounded-xl border px-4 py-3.5 text-center ` +
      `text-base font-bold transition-colors duration-300 ${tones[banner.tone] || tones.wait}`
    this.turnBannerTextTarget.textContent = banner.text
    if (this.hasTurnBannerIconTarget) {
      // マイクアイコンは音声で話せる場面だけ出す（アイコン自体がターンの目印になる）
      this.turnBannerIconTarget.classList.toggle("hidden", !banner.micIcon)
      this.turnBannerIconTarget.classList.toggle("animate-pulse", Boolean(banner.micIcon))
    }
  }

  showSubtitle(text) {
    this.subtitleTextTarget.textContent = text
    this.subtitleTarget.classList.remove("hidden")
  }

  showTextForm(visible) {
    this.textFormTarget.classList.toggle("hidden", !visible)
    this.toggleTextLinkTarget.textContent = visible ? "テキスト入力を閉じる" : "テキストで回答する"
    if (visible) this.textInputTarget.focus()
  }

  appendMessage(speaker, text) {
    const wrapper = document.createElement("div")
    if (speaker === "ai") {
      wrapper.className = "flex gap-2"
      const aiName = this.aiNameValue || "田中"
      wrapper.innerHTML = `
        <span class="shrink-0 text-xs font-medium text-indigo-600 mt-0.5">${aiName}</span>
        <p class="rounded-lg bg-indigo-50 px-3 py-2 text-sm text-gray-800"></p>`
    } else {
      wrapper.className = "flex flex-row-reverse gap-2"
      wrapper.innerHTML = `<p class="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800"></p>`
    }
    wrapper.querySelector("p").textContent = text
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  appendNotice(message) {
    const div = document.createElement("div")
    div.className = "rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 text-center"
    div.textContent = message
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  updateProgress(answered, total) {
    if (answered == null || !total) return
    const percent = Math.round((answered / total) * 100)
    this.progressBarTarget.style.width = `${percent}%`
    const remaining = total - answered
    const unit = this.progressUnitValue || "問"
    this.progressLabelTarget.textContent = remaining > 0 ? `残り${remaining}${unit}` : "完了"
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
